import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPLOAD_PAYMENT_PROOF } from '../graphql/mutations';
import Layout from '../components/Layout';
import QRCode from 'react-qr-code';
import { Card, Button, Alert, Row, Col, Form } from 'react-bootstrap';
import { Lightning, CheckCircle, CloudArrowUp, XCircle } from 'react-bootstrap-icons';

const BoostPayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [paymentProof, setPaymentProof] = useState<string>('');
  const [previewImage, setPreviewImage] = useState<string>('');
  const [transactionId, setTransactionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [uploadPaymentProof, { loading: uploading }] = useMutation(UPLOAD_PAYMENT_PROOF, {
    onCompleted: (data) => {
      if (data.uploadPaymentProof.success) {
        setSuccess('Payment proof uploaded successfully! Your boost request is pending admin approval.');
        setError('');
      } else {
        setError(data.uploadPaymentProof.message);
        setSuccess('');
      }
    },
    onError: (err) => {
      setError(err.message);
      setSuccess('');
    }
  });

  // In a real app, you'd fetch the boost request details
  // For now, we'll use mock data
  const boostRequest = {
    id: id,
    amount: '700', // This would come from the boost request
    durationDays: 7,
    ad: {
      title: 'Sample Ad Title'
    }
  };

  // Company bank details - in a real app, these would come from settings
  const bankDetails = {
    bankName: 'Nepal Investment Bank',
    accountName: 'NayaBajar Pvt Ltd',
    accountNumber: '1234567890123',
    qrCode: 'nayabajar-payment' // This would be a real QR code data
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPreviewImage(base64String);
      setPaymentProof(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPaymentProof('');
    setPreviewImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!paymentProof) {
      setError('Please upload payment proof');
      return;
    }

    if (!transactionId.trim()) {
      setError('Please enter transaction ID');
      return;
    }

    uploadPaymentProof({
      variables: {
        boostRequestId: id,
        paymentProof: paymentProof,
        transactionId: transactionId.trim()
      }
    });
  };

  return (
    <Layout showSidebar={false}>
      <div className="container py-4">
        <div className="mb-4">
          <h1 className="display-6 fw-bold mb-2">Complete Payment</h1>
          <p className="text-muted">
            Scan the QR code and upload payment proof to activate your boost
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <Card className="shadow-sm">
            <Card.Body className="p-5">
              <div className="text-center">
                <div className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10" style={{ width: '64px', height: '64px' }}>
                  <CheckCircle className="text-success" style={{ width: '32px', height: '32px' }} />
                </div>
                <h2 className="fw-bold mb-2">Payment Proof Uploaded!</h2>
                <p className="text-muted mb-4">{success}</p>
                <Alert variant="primary" className="mb-4">
                  <h5 className="fw-bold text-primary-emphasis mb-2">What happens next?</h5>
                  <ul className="text-start small text-primary-emphasis mb-0">
                    <li>✓ Our team will verify your payment within 24 hours</li>
                    <li>✓ You'll receive a notification once approved</li>
                    <li>✓ Your ad will be featured immediately after approval</li>
                    <li>✓ You can track the status from your profile</li>
                  </ul>
                </Alert>
                <Button
                  variant="primary"
                  className="px-5 py-3 fw-semibold"
                  onClick={() => navigate('/profile')}
                >
                  Go to My Profile
                </Button>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {/* Payment Details */}
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h2 className="h5 fw-semibold mb-4 d-flex align-items-center gap-2">
                    <Lightning className="text-primary" />
                    Payment Details
                  </h2>

                  <Alert variant="primary" className="mb-4">
                    <div className="text-center">
                      <p className="small text-primary-emphasis mb-2">Amount to Pay</p>
                      <p className="display-6 fw-bold text-primary">Rs. {boostRequest.amount}</p>
                      <p className="small text-primary-emphasis mt-2">for {boostRequest.durationDays} days</p>
                    </div>
                  </Alert>

                  <div className="mb-4">
                    <h5 className="fw-semibold mb-3">Scan QR Code</h5>
                    <div className="bg-white border border-2 border-dashed rounded p-3 d-flex justify-content-center">
                      <div style={{ width: 200, height: 200 }}>
                        <QRCode
                          value={bankDetails.qrCode}
                          size={200}
                          style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                        />
                      </div>
                    </div>
                    <p className="small text-muted text-center mt-2">
                      Scan with any mobile payment app (eSewa, Khalti, IME Pay, etc.)
                    </p>
                  </div>

                  <div className="border-top pt-4 mb-4">
                    <h5 className="fw-semibold mb-3">Bank Transfer Details</h5>
                    <div className="d-flex flex-column gap-2 small">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Bank Name:</span>
                        <span className="fw-medium">{bankDetails.bankName}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Account Name:</span>
                        <span className="fw-medium">{bankDetails.accountName}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Account Number:</span>
                        <span className="fw-medium font-monospace">{bankDetails.accountNumber}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-top pt-4">
                    <h5 className="fw-semibold mb-2">Important Instructions</h5>
                    <ul className="small text-muted mb-0">
                      <li>• Payment must be made from your registered name</li>
                      <li>• Include the transaction ID in your payment description</li>
                      <li>• Upload clear screenshot of payment confirmation</li>
                      <li>• Verification takes 1-24 hours</li>
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Upload Form */}
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h2 className="h5 fw-semibold mb-4">Upload Payment Proof</h2>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-medium">Transaction ID *</Form.Label>
                      <Form.Control
                        type="text"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        placeholder="Enter transaction ID from payment receipt"
                        required
                      />
                      <Form.Text className="text-muted">
                        This is the reference number from your payment app/bank
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-medium">Payment Screenshot *</Form.Label>
                      {!previewImage ? (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="border border-2 border-dashed rounded p-5 text-center text-muted cursor-pointer hover-border-primary"
                          style={{ cursor: 'pointer', borderColor: 'var(--bs-border-color)' }}
                          onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--bs-primary)'}
                          onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--bs-border-color)'}
                        >
                          <CloudArrowUp className="d-block mx-auto mb-3" style={{ width: '48px', height: '48px' }} />
                          <p className="small mb-1">
                            Click to upload or drag and drop
                          </p>
                          <p className="small text-muted">
                            PNG, JPG, GIF up to 5MB
                          </p>
                          <Form.Control
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="d-none"
                          />
                        </div>
                      ) : (
                        <div className="position-relative">
                          <img
                            src={previewImage}
                            alt="Payment proof preview"
                            className="img-fluid rounded border"
                            style={{ height: '256px', objectFit: 'cover', width: '100%' }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-2 rounded-circle"
                            onClick={handleRemoveImage}
                          >
                            <XCircle style={{ width: '20px', height: '20px' }} />
                          </Button>
                        </div>
                      )}
                    </Form.Group>

                    <Alert variant="warning" className="mb-4">
                      <p className="small text-warning-emphasis mb-0">
                        <strong>Note:</strong> Make sure the screenshot clearly shows the transaction ID, amount,
                        and recipient details. Blurry or incomplete screenshots will be rejected.
                      </p>
                    </Alert>

                    <Button
                      variant={uploading || !paymentProof ? 'secondary' : 'primary'}
                      type="submit"
                      className="w-100 py-3 fw-semibold"
                      disabled={uploading || !paymentProof}
                    >
                      {uploading ? 'Uploading...' : 'Submit Payment Proof'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </Layout>
  );
};

export default BoostPayment;
